<template>
  <div class="p-6 space-y-6">
    <!-- Header Principal -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Target class="w-7 h-7 text-brand-500" />
          Pipeline de Leads
        </h1>
        <p class="text-sm text-slate-500 dark:text-slate-400">
          Gestiona prospectos analizados y calificados por tus agentes.
        </p>
      </div>

      <!-- Filtro por estado -->
      <div class="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0">
        <button
          v-for="st in statusOptions"
          :key="st.value"
          @click="currentStatusFilter = st.value"
          class="px-3 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 whitespace-nowrap"
          :class="[
            currentStatusFilter === st.value
              ? 'bg-slate-900 text-white dark:bg-brand-500 shadow-sm'
              : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300'
          ]"
        >
          <span class="w-2 h-2 rounded-full" :class="st.color"></span>
          {{ st.label }}
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center py-16">
      <Loader2 class="w-8 h-8 text-brand-500 animate-spin" />
    </div>

    <!-- Empty State -->
    <div v-else-if="!parsedLeads || parsedLeads.length === 0" class="text-center py-16 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl p-8">
      <UserX class="w-12 h-12 text-slate-400 mx-auto mb-3" />
      <h3 class="text-base font-bold text-slate-800 dark:text-slate-200">No hay leads en este estado</h3>
      <p class="text-xs text-slate-500 mt-1">El pipeline está vacío para este filtro.</p>
    </div>

    <!-- Grid de Leads -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      <div
        v-for="lead in parsedLeads"
        :key="lead.id"
        class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl p-5 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4 relative overflow-hidden"
      >
        <div class="absolute left-0 top-0 bottom-0 w-1" :class="getStatusColor(lead.status)"></div>

        <div class="space-y-3 pl-2">
          <!-- Top info & Status -->
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <Bot class="w-3 h-3" /> {{ lead.instance_name }}
              </span>
              <h3 class="text-base font-bold text-slate-900 dark:text-white truncate">
                {{ lead.parsedData?.extractedName || 'Prospecto' }}
              </h3>
              <p class="text-xs font-medium text-slate-500 flex items-center gap-1 mt-0.5">
                <Phone class="w-3 h-3 text-slate-400" /> +{{ lead.phone }}
              </p>
            </div>

            <span
              class="text-[11px] font-bold px-2.5 py-1 rounded-lg border flex items-center gap-1.5 shrink-0"
              :class="getStatusBadgeClass(lead.status)"
            >
              <span class="w-1.5 h-1.5 rounded-full" :class="getStatusColor(lead.status)"></span>
              {{ getStatusLabel(lead.status) }}
            </span>
          </div>

          <!-- Badges de Interés & Producto -->
          <div class="flex flex-wrap gap-1.5 items-center">
            <span
              v-if="lead.parsedData?.interest"
              class="text-[11px] font-semibold px-2 py-0.5 rounded-md"
              :class="getInterestBadgeColor(lead.parsedData.interest)"
            >
              Interés: {{ lead.parsedData.interest }}
            </span>

            <span
              v-if="lead.parsedData?.product"
              class="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border border-purple-200/60 dark:border-purple-800/40 flex items-center gap-1 truncate max-w-[200px]"
            >
              <ShoppingBag class="w-3 h-3" /> {{ lead.parsedData.product }}
            </span>
          </div>

          <!-- Email -->
          <div v-if="lead.email" class="flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900/50 p-2 rounded-lg">
            <Mail class="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span class="truncate">{{ lead.email }}</span>
          </div>

          <!-- AI Intention Insight -->
          <div class="bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/50 rounded-xl p-3 text-xs space-y-1">
            <p class="font-bold text-indigo-900 dark:text-indigo-300 flex items-center gap-1.5 text-[11px] uppercase tracking-wide">
              <Brain class="w-3.5 h-3.5 text-indigo-500" /> Intención Detectada
            </p>
            <p class="text-slate-700 dark:text-slate-300 line-clamp-3 leading-relaxed">
              {{ lead.parsedData?.intention || 'Sin intención especificada.' }}
            </p>
          </div>
        </div>

        <!-- Footer Card -->
        <div class="pt-3 pl-2 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between text-[11px] text-slate-400">
          <span>{{ formatDate(lead.created_at) }}</span>
          <div class="flex gap-2">
            <button
              @click="openLeadDetails(lead)"
              class="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-brand-500 hover:text-white font-bold transition-all flex items-center gap-1"
            >
              <Eye class="w-3.5 h-3.5" /> Ficha CRM
            </button>
            <button
              @click="openWhatsApp(lead.phone)"
              class="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-200/60 hover:bg-emerald-600 hover:text-white font-bold transition-all flex items-center gap-1"
            >
              <MessageSquare class="w-3.5 h-3.5" /> Chat
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL CRM PRO CON PESTAÑAS -->
    <div v-if="isDetailsModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-slate-950/70 backdrop-blur-sm">
      <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">

        <!-- Header del Modal -->
        <div class="p-5 sm:px-6 border-b border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row justify-between sm:items-center gap-4 bg-slate-50 dark:bg-slate-800/40">
          <div class="flex items-center gap-3.5">
            <div class="w-12 h-12 rounded-2xl bg-brand-500/10 text-brand-600 dark:text-brand-400 flex items-center justify-center font-bold text-xl border border-brand-500/20">
              {{ (selectedLead.parsedData?.extractedName || 'C').charAt(0).toUpperCase() }}
            </div>
            <div>
              <div class="flex items-center gap-2">
                <h2 class="text-lg font-bold text-slate-900 dark:text-white">
                  {{ selectedLead.parsedData?.extractedName || 'Cliente WA' }}
                </h2>
                <Loader2 v-if="isLoadingClient" class="w-3.5 h-3.5 text-brand-500 animate-spin" />
              </div>
              <p class="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                <span class="flex items-center gap-1 font-medium"><Phone class="w-3 h-3 text-slate-400" /> +{{ selectedLead.phone }}</span>
                <span>•</span>
                <span class="flex items-center gap-1 font-medium"><Mail class="w-3 h-3 text-slate-400" /> {{ clientData.email || selectedLead.email || 'Sin correo' }}</span>
              </p>
            </div>
          </div>

          <!-- Acciones Rápidas -->
          <div class="flex items-center gap-2 self-end sm:self-center">
            <button
              @click="openWhatsApp(selectedLead.phone)"
              class="px-3.5 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
            >
              <MessageSquare class="w-3.5 h-3.5" /> WhatsApp
            </button>
            <a
              v-if="selectedLead.email"
              :href="`mailto:${selectedLead.email}`"
              class="px-3.5 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 text-slate-700 dark:text-slate-200 text-xs font-bold transition-all flex items-center gap-1.5"
            >
              <Mail class="w-3.5 h-3.5" /> Correo
            </a>
            <button @click="isDetailsModalOpen = false" class="p-1.5 rounded-xl text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all">
              <X class="w-5 h-5" />
            </button>
          </div>
        </div>

        <!-- Barra Interactiva de Estados -->
        <div class="px-6 py-3 border-b border-slate-100 dark:border-slate-800/80 bg-white dark:bg-slate-900 flex flex-wrap items-center justify-between gap-3">
          <div class="flex items-center gap-2 text-xs font-semibold text-slate-500">
            <span>Fase del Lead:</span>
            <div class="inline-flex rounded-xl bg-slate-100 dark:bg-slate-800 p-0.5 border border-slate-200 dark:border-slate-700">
              <button
                v-for="st in availableStatuses"
                :key="st.value"
                @click="updateLeadStatusDirectly(st.value)"
                class="px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5"
                :class="[
                  selectedLead.status === st.value
                    ? getActiveStatusButtonClass(st.value)
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                ]"
              >
                <span class="w-1.5 h-1.5 rounded-full" :class="st.color"></span>
                {{ st.label }}
              </button>
            </div>
          </div>

          <span class="text-xs text-slate-400 flex items-center gap-1">
            <Clock class="w-3.5 h-3.5" /> Ingreso: {{ formatDate(selectedLead.created_at) }}
          </span>
        </div>

        <!-- Selector de Pestañas -->
        <div class="px-6 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900 flex gap-4">
          <button
            @click="activeModalTab = 'details'"
            class="py-3 text-xs font-bold border-b-2 flex items-center gap-2 transition-all"
            :class="[
              activeModalTab === 'details'
                ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            ]"
          >
            <Brain class="w-4 h-4" /> Ficha, IA & Notas
          </button>

          <button
            @click="activeModalTab = 'chat'"
            class="py-3 text-xs font-bold border-b-2 flex items-center gap-2 transition-all"
            :class="[
              activeModalTab === 'chat'
                ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            ]"
          >
            <MessageSquare class="w-4 h-4" /> Historial de Chat
            <span class="px-1.5 py-0.5 rounded-full text-[10px] bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
              {{ chatHistory.length }}
            </span>
          </button>
        </div>

        <!-- Cuerpo del Modal -->
        <div class="p-6 overflow-y-auto flex-1 bg-slate-50/50 dark:bg-slate-950/40">

          <!-- PESTAÑA 1: FICHA, RESUMEN IA Y NOTAS LARGAS -->
          <div v-if="activeModalTab === 'details'" class="space-y-5">

            <!-- TARJETA DINÁMICA DE CIERRE: GANADA -->
            <div
              v-if="selectedLead.status === 'won'"
              class="p-5 rounded-2xl border border-emerald-300 dark:border-emerald-700/60 bg-emerald-50/80 dark:bg-emerald-950/30 space-y-4"
            >
              <div class="flex items-center justify-between">
                <h4 class="text-xs font-bold text-emerald-900 dark:text-emerald-300 uppercase tracking-wide flex items-center gap-1.5">
                  <CheckCircle2 class="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Registro de Venta Ganada
                </h4>
                <button
                  @click="saveCrmData"
                  :disabled="isSavingNotes"
                  class="px-3.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-all flex items-center gap-1 shadow-sm"
                >
                  <Loader2 v-if="isSavingNotes" class="w-3.5 h-3.5 animate-spin" />
                  <Check v-else class="w-3.5 h-3.5" /> Guardar Venta
                </button>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div class="sm:col-span-1">
                  <label class="text-[11px] font-bold text-emerald-800 dark:text-emerald-300 mb-1.5 block">Monto Cobrado (C$ / $)</label>
                  <input
                    type="number"
                    v-model="selectedLead.deal_value"
                    placeholder="0.00"
                    class="w-full px-3.5 py-2 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-xs font-bold focus:ring-2 focus:ring-emerald-500 outline-none"
                  />
                </div>
                <div class="sm:col-span-2">
                  <label class="text-[11px] font-bold text-emerald-800 dark:text-emerald-300 mb-1.5 block">Notas de Cierre / Factura / Detalles</label>
                  <textarea
                    rows="3"
                    v-model="selectedLead.notes"
                    placeholder="Escribe aquí los acuerdos, productos entregados o plan contratado..."
                    class="w-full px-3.5 py-2 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-emerald-500 outline-none resize-y"
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- TARJETA DINÁMICA DE CIERRE: CANCELADA -->
            <div
              v-else-if="selectedLead.status === 'lost'"
              class="p-5 rounded-2xl border border-red-300 dark:border-red-700/60 bg-red-50/80 dark:bg-red-950/30 space-y-4"
            >
              <div class="flex items-center justify-between">
                <h4 class="text-xs font-bold text-red-900 dark:text-red-300 uppercase tracking-wide flex items-center gap-1.5">
                  <XCircle class="w-4 h-4 text-red-600 dark:text-red-400" /> Registro de Cancelación / Descarte
                </h4>
                <button
                  @click="saveCrmData"
                  :disabled="isSavingNotes"
                  class="px-3.5 py-1.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition-all flex items-center gap-1 shadow-sm"
                >
                  <Loader2 v-if="isSavingNotes" class="w-3.5 h-3.5 animate-spin" />
                  <Check v-else class="w-3.5 h-3.5" /> Guardar Motivo
                </button>
              </div>

              <div>
                <label class="text-[11px] font-bold text-red-800 dark:text-red-300 mb-1.5 block">Motivo Detallado de Cancelación / Rechazo</label>
                <textarea
                  rows="3"
                  v-model="selectedLead.notes"
                  placeholder="Detalla por qué no se concretó la venta (precio fuera de presupuesto, no volvió a contestar, optó por otra opción, etc.)..."
                  class="w-full px-3.5 py-2 rounded-xl border border-red-200 dark:border-red-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-red-500 outline-none resize-y"
                ></textarea>
              </div>
            </div>

            <!-- SEGUIMIENTO HABITUAL: NUEVO O EN NEGOCIACIÓN -->
            <div
              v-else
              class="space-y-4 p-5 rounded-2xl bg-white dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/60 shadow-xs"
            >
              <div class="flex items-center justify-between">
                <h4 class="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wide flex items-center gap-1.5">
                  <FileText class="w-3.5 h-3.5 text-brand-500" /> Seguimiento Comercial & Cotización
                </h4>
                <button
                  @click="saveCrmData"
                  :disabled="isSavingNotes"
                  class="px-3.5 py-1.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white text-xs font-bold transition-all flex items-center gap-1 shadow-sm"
                >
                  <Loader2 v-if="isSavingNotes" class="w-3.5 h-3.5 animate-spin" />
                  <Check v-else class="w-3.5 h-3.5" /> Guardar Seguimiento
                </button>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div class="sm:col-span-1">
                  <label class="text-[11px] font-bold text-slate-500 mb-1.5 block">Valor Estimado Cotización</label>
                  <input
                    type="number"
                    v-model="selectedLead.deal_value"
                    placeholder="0.00"
                    class="w-full px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900 text-slate-900 dark:text-white text-xs font-semibold focus:ring-2 focus:ring-brand-500 outline-none"
                  />
                </div>
                <div class="sm:col-span-2">
                  <label class="text-[11px] font-bold text-slate-500 mb-1.5 block">Bitácora / Notas de Seguimiento</label>
                  <textarea
                    rows="3"
                    v-model="selectedLead.notes"
                    placeholder="Escribe aquí acuerdos con el cliente, fecha de próxima llamada, dudas planteadas..."
                    class="w-full px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900 text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-brand-500 outline-none resize-y"
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- Metadata Cards -->
            <div class="grid grid-cols-3 gap-3">
              <div class="p-3.5 bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-200/80 dark:border-slate-700/60 shadow-xs">
                <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Interés</span>
                <span
                  class="text-xs font-bold px-2 py-0.5 rounded-md inline-block"
                  :class="getInterestBadgeColor(selectedLead.parsedData?.interest)"
                >
                  {{ selectedLead.parsedData?.interest || 'No clasificado' }}
                </span>
              </div>

              <div class="p-3.5 bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-200/80 dark:border-slate-700/60 shadow-xs">
                <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Producto</span>
                <p class="text-xs font-bold text-slate-800 dark:text-slate-200 truncate" :title="selectedLead.parsedData?.product">
                  {{ selectedLead.parsedData?.product || 'No especificado' }}
                </p>
              </div>

              <div class="p-3.5 bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-200/80 dark:border-slate-700/60 shadow-xs">
                <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Instancia</span>
                <p class="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1">
                  <Bot class="w-3.5 h-3.5 text-brand-500" /> {{ selectedLead.instance_name }}
                </p>
              </div>
            </div>

            <!-- Resumen de IA Estilizado -->
            <div class="space-y-2">
              <h4 class="text-xs font-bold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <Brain class="w-3.5 h-3.5 text-indigo-500" /> Análisis Conversacional del Agente
              </h4>
              <div
                class="p-4 rounded-2xl bg-white dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/60 text-sm text-slate-700 dark:text-slate-300 leading-relaxed shadow-xs summary-content"
                v-html="renderMarkdown(selectedLead.ai_summary)"
              ></div>
            </div>

          </div>

          <!-- PESTAÑA 2: HISTORIAL DE CHAT COMPLETO -->
          <div v-else-if="activeModalTab === 'chat'" class="space-y-3">
            <div class="bg-white dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/60 rounded-2xl p-4 flex flex-col justify-between overflow-hidden shadow-xs min-h-[450px]">

              <div v-if="isLoadingChat" class="flex flex-col items-center justify-center flex-1 py-16">
                <Loader2 class="w-7 h-7 text-brand-500 animate-spin mb-2" />
                <span class="text-xs text-slate-400 font-medium">Cargando transcripción de WhatsApp...</span>
              </div>

              <div v-else-if="!chatHistory || chatHistory.length === 0" class="flex flex-col items-center justify-center flex-1 text-center py-16">
                <MessageSquareOff class="w-10 h-10 text-slate-300 dark:text-slate-600 mb-2" />
                <p class="text-sm font-semibold text-slate-600 dark:text-slate-400">Sin mensajes registrados</p>
                <p class="text-xs text-slate-400 mt-0.5">No se encontró historial en la base de datos para esta sesión.</p>
              </div>

              <!-- Burbujas del Chat con Scroll -->
              <div v-else class="space-y-3 overflow-y-auto pr-2 max-h-[500px]">
                <div
                  v-for="msg in chatHistory"
                  :key="msg.id"
                  class="flex w-full"
                  :class="msg.sender === 'user' ? 'justify-end' : 'justify-start'"
                >
                  <div
                    class="max-w-[80%] p-3.5 rounded-2xl text-xs leading-relaxed"
                    :class="msg.sender === 'user' ? 'bg-brand-500 text-white rounded-tr-xs shadow-xs' : 'bg-slate-100 dark:bg-slate-700/70 text-slate-800 dark:text-slate-200 rounded-tl-xs'"
                  >
                    <p class="text-[9px] opacity-70 mb-1 font-bold uppercase tracking-wider">
                      {{ msg.sender === 'user' ? (selectedLead.parsedData?.extractedName || 'Cliente') : 'Agente' }}
                    </p>
                    <p class="whitespace-pre-line">{{ msg.text }}</p>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>

      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import {
  Target, Mail, Brain, MessageSquare, Loader2, UserX, Eye, Bot, X,
  Phone, ShoppingBag, Clock, FileText, Check, MessageSquareOff,
  CheckCircle2, XCircle
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';
import { SecureStorage } from '@/utils/secureStorage';

const { showSuccess, showError } = useToast();

const leads = ref([]);
const isLoading = ref(true);
const currentStatusFilter = ref('all');

// Modales, Tabs & Data
const isDetailsModalOpen = ref(false);
const activeModalTab = ref('details');
const selectedLead = ref({});
const clientData = ref({});
const isLoadingClient = ref(false);

const chatHistory = ref([]);
const isLoadingChat = ref(false);
const isSavingNotes = ref(false);

const statusOptions = [
  { value: 'all', label: 'Todos', color: 'bg-slate-400' },
  { value: 'new', label: 'Nuevos', color: 'bg-yellow-400' },
  { value: 'in_progress', label: 'En Negociación', color: 'bg-blue-500' },
  { value: 'won', label: 'Ganados', color: 'bg-emerald-500' },
  { value: 'lost', label: 'Cancelados', color: 'bg-red-500' }
];

const availableStatuses = [
  { value: 'new', label: '🟡 Nuevo', color: 'bg-yellow-400' },
  { value: 'in_progress', label: '🔵 En Negociación', color: 'bg-blue-500' },
  { value: 'won', label: '🟢 Ganada', color: 'bg-emerald-500' },
  { value: 'lost', label: '🔴 Cancelada', color: 'bg-red-500' }
];

const currentUser = SecureStorage.getItem('neurals_user') || SecureStorage.getItem('llevo_user');

// Formateador Markdown
const renderMarkdown = (text) => {
  if (!text) return '<span class="text-slate-400 italic">Sin resumen disponible</span>';

  let formatted = text
    .replace(/\*\*Resumen Ejecutivo\*\*/gi, '')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-slate-900 dark:text-white">$1</strong>')
    .replace(/^\s{2,}[-*]\s*(.*)$/gm, '<li class="ml-5 list-disc text-slate-600 dark:text-slate-400 my-0.5">$1</li>')
    .replace(/^[-*]\s*(.*)$/gm, '<li class="list-disc ml-4 my-1">$1</li>')
    .replace(/\n\s*\n/g, '<div class="h-2"></div>')
    .replace(/\n/g, '<br/>');

  return formatted.trim();
};

// Parser de datos del lead
const parseSummary = (lead) => {
  if (!lead) return { extractedName: 'Prospecto', intention: '', interest: '', product: '' };

  const summary = lead.ai_summary || '';

  let extractedName = lead.full_name || '';
  const nameMatch = summary.match(/Nombre:\s*([^\n\r]+)/i) || summary.match(/El cliente\s*\(([^)]+)\)/i);
  if (nameMatch && nameMatch[1]) {
    extractedName = nameMatch[1].trim();
  } else if (!extractedName || extractedName.startsWith('Lead WA')) {
    extractedName = `Prospecto (+${(lead.phone || '').slice(-4)})`;
  }

  let intention = '';
  const intentionMatch = summary.match(/[-*]\s*\*\*Intenci[oó]n\*\*:\s*([^\n\r]+)/i);
  if (intentionMatch && intentionMatch[1]) {
    intention = intentionMatch[1].trim();
  }

  let interest = '';
  const interestMatch = summary.match(/[-*]\s*\*\*Nivel de Inter[eé]s\*\*:\s*([^\n\r–-]+)/i);
  if (interestMatch && interestMatch[1]) {
    interest = interestMatch[1].trim();
  }

  let product = '';
  const productMatch = summary.match(/Producto ofrecido:\s*\*\*?([^\n\r*]+)\*\*?/i);
  if (productMatch && productMatch[1]) {
    product = productMatch[1].trim();
  }

  return {
    extractedName,
    intention,
    interest,
    product
  };
};

const parsedLeads = computed(() => {
  if (!Array.isArray(leads.value)) return [];

  const list = currentStatusFilter.value === 'all'
    ? leads.value
    : leads.value.filter(l => l && l.status === currentStatusFilter.value);

  return list.map(lead => ({
    ...lead,
    parsedData: parseSummary(lead)
  }));
});

const fetchLeads = async () => {
  isLoading.value = true;
  try {
    const params = {};
    if (currentUser && currentUser?.role !== 'admin') {
      params.owner = currentUser?.id;
    }
    const res = await apiClient.get('/leads', { params });
    leads.value = Array.isArray(res.data) ? res.data : (res.data?.data || []);
  } catch (err) {
    showError('Error al cargar la lista de leads.');
    leads.value = [];
  } finally {
    isLoading.value = false;
  }
};

const openLeadDetails = async (lead) => {
  selectedLead.value = { ...lead };
  activeModalTab.value = 'details';
  isDetailsModalOpen.value = true;
  isLoadingChat.value = true;
  isLoadingClient.value = true;
  chatHistory.value = [];
  clientData.value = {};

  // 1. Cargar datos del cliente
  try {
    const userRes = await apiClient.get(`/users/${lead.user}`);
    clientData.value = userRes.data?.data || userRes.data || {};
  } catch (e) {
    console.error("No se pudo cargar la data extra del cliente", e);
  } finally {
    isLoadingClient.value = false;
  }

  // 2. Cargar historial de mensajes
  try {
    const phoneOnly = lead.phone;
    const currentAgentId = lead.instance_name;

    const res = await apiClient.get(`get_conversations?phone=${phoneOnly}&instance_name=${currentAgentId}`);
    const dbMessages = Array.isArray(res.data) ? res.data : (res.data?.data || []);

    chatHistory.value = dbMessages.map(msg => ({
      id: Math.random().toString(),
      sender: msg.role === 'assistant' ? 'bot' : 'user',
      text: msg.text || msg.message || '',
      type: 'text',
      created_at: msg.created_at ? new Date(msg.created_at).toISOString() : new Date().toISOString()
    }));

  } catch (error) {
    console.error(error);
    showError('No se pudieron cargar los mensajes.');
    chatHistory.value = [];
  } finally {
    isLoadingChat.value = false;
  }
};

// Acciones de Pipeline desde el Modal
const updateLeadStatusDirectly = async (newStatus) => {
  if (!selectedLead.value) return;
  selectedLead.value.status = newStatus;

  if (newStatus === 'lost') {
    selectedLead.value.deal_value = 0;
  }

  const index = leads.value.findIndex(l => l.id === selectedLead.value.id);
  if (index !== -1) {
    leads.value[index].status = newStatus;
    if (newStatus === 'lost') {
      leads.value[index].deal_value = 0;
    }
  }

  try {
    await apiClient.put(`/leads/${selectedLead.value.id}`, {
      status: newStatus,
      notes: selectedLead.value.notes || '',
      deal_value: selectedLead.value.deal_value || 0
    });
    showSuccess(`Estado actualizado a: ${getStatusLabel(newStatus)}`);
  } catch (err) {
    showError('No se pudo actualizar el estado');
  }
};

const saveCrmData = async () => {
  if (!selectedLead.value) return;
  isSavingNotes.value = true;

  try {
    await apiClient.put(`/leads/${selectedLead.value.id}`, {
      status: selectedLead.value.status,
      notes: selectedLead.value.notes || '',
      deal_value: selectedLead.value.deal_value || 0
    });

    const index = leads.value.findIndex(l => l.id === selectedLead.value.id);
    if (index !== -1) {
      leads.value[index].notes = selectedLead.value.notes;
      leads.value[index].deal_value = selectedLead.value.deal_value;
    }

    showSuccess('Información guardada correctamente');
  } catch (err) {
    showError('Error al guardar notas');
  } finally {
    isSavingNotes.value = false;
  }
};

// Utilidades Visuales
const getStatusColor = (status) => {
  const colors = {
    'new': 'bg-yellow-400',
    'in_progress': 'bg-blue-500',
    'won': 'bg-emerald-500',
    'lost': 'bg-red-500'
  };
  return colors[status] || 'bg-slate-200';
};

const getStatusLabel = (status) => {
  const labels = {
    'new': 'Nuevo',
    'in_progress': 'En Negociación',
    'won': 'Ganada',
    'lost': 'Cancelada'
  };
  return labels[status] || status;
};

const getStatusBadgeClass = (status) => {
  const classes = {
    'new': 'bg-yellow-50 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-400 border-yellow-200/60 dark:border-yellow-800/40',
    'in_progress': 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 border-blue-200/60 dark:border-blue-800/40',
    'won': 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border-emerald-200/60 dark:border-emerald-800/40',
    'lost': 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400 border-red-200/60 dark:border-red-800/40'
  };
  return classes[status] || 'bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200';
};

const getActiveStatusButtonClass = (status) => {
  const classes = {
    'new': 'bg-yellow-400 text-slate-900 shadow-xs',
    'in_progress': 'bg-blue-600 text-white shadow-xs',
    'won': 'bg-emerald-600 text-white shadow-xs',
    'lost': 'bg-red-600 text-white shadow-xs'
  };
  return classes[status] || 'bg-slate-900 text-white';
};

const getInterestBadgeColor = (interestText) => {
  const lower = (interestText || '').toLowerCase();
  if (lower.includes('alto') || lower.includes('alta')) {
    return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-800/40';
  }
  if (lower.includes('medio') || lower.includes('media')) {
    return 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200/60 dark:border-amber-800/40';
  }
  return 'bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700';
};

const openWhatsApp = (phone) => {
  window.open(`https://wa.me/${phone}`, '_blank');
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-NI', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

onMounted(() => {
  fetchLeads();
});
</script>

<style scoped>
.summary-content :deep(strong) {
  font-weight: 600;
}
.summary-content :deep(li) {
  margin-bottom: 0.25rem;
}
</style>